import * as fs from 'fs';
import * as path from 'path';
import { fileURLToPath } from 'url';

const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);

/**
 * Agent 元数据
 */
export interface AgentMetadata {
    id: string;
    title: string;
    description: string;
    tags: string[];
}

/**
 * 从 Agent 文件中提取元数据
 */
function parseAgentFile(filePath: string): AgentMetadata | null {
    try {
        const content = fs.readFileSync(filePath, 'utf-8');
        const lines = content.split('\n');
        
        let title = '';
        let description = '';
        const tags: string[] = [];
        
        // 查找第一个 # 标题
        const titleLine = lines.find(l => l.trim().startsWith('# '));
        if (titleLine) {
            title = titleLine.replace(/^#\s*/, '').trim();
        }
        
        // 查找 > 描述行
        const descLine = lines.find(l => l.trim().startsWith('> '));
        if (descLine) {
            description = descLine.replace(/^>\s*/, '').trim();
        }
        
        // 从文件名提取 ID
        const fileName = path.basename(filePath);
        const id = fileName.replace('.agent.md', '');
        
        // 查找标签
        const tagsLine = lines.find(l => 
            l.includes('**标签**') || 
            l.includes('**关键词**') ||
            l.includes('**Tags**')
        );
        if (tagsLine) {
            const tagsMatch = tagsLine.match(/[:：]\s*(.+)/);
            if (tagsMatch) {
                tags.push(...tagsMatch[1].split(/[,，、]/).map(t => t.trim()));
            }
        }
        
        return {
            id,
            title: title || id,
            description: description || '暂无描述',
            tags,
        };
    } catch (error) {
        console.error(`解析 Agent 文件失败: ${filePath}`, error);
        return null;
    }
}

/**
 * 列出所有可用的 Agents
 */
export function listAvailableAgents(): { agents: AgentMetadata[] } {
    try {
        // agents 目录在 src 的同级（项目根目录）
        const projectRoot = path.resolve(__dirname, '../..');
        const agentsDir = path.join(projectRoot, 'agents');
        
        if (!fs.existsSync(agentsDir)) {
            throw new Error(`Agents 目录不存在: ${agentsDir}`);
        }
        
        // 读取所有 .agent.md 文件
        const agentFiles = fs.readdirSync(agentsDir)
            .filter(f => f.endsWith('.agent.md') && !f.endsWith('.bak'))
            .map(f => path.join(agentsDir, f));
        
        // 解析所有 Agent 文件
        const agents: AgentMetadata[] = [];
        for (const filePath of agentFiles) {
            const metadata = parseAgentFile(filePath);
            if (metadata) {
                agents.push(metadata);
            }
        }
        
        return {
            agents: agents.sort((a, b) => a.title.localeCompare(b.title))
        };
    } catch (error) {
        console.error('列出 Agents 失败:', error);
        return { agents: [] };
    }
}
